import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Wallet, RefreshCw, ChevronLeft, ChevronRight,
    Package, TrendingUp,
    X, Calendar, Clock, Percent, Eye, CheckCircle,
    Upload, FileText, Loader2
} from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS } from '../../config/environment';
import { formatTL, isAmountWithinTolerance, hasInvoiceableAmount } from '../../utils/money';
import { getFleetPaymentStatus, canUploadFleetInvoice } from '../../utils/paymentStatus';
import TlIcon from '../../components/TlIcon';
import FleetPaymentBreakdown from './FleetPaymentBreakdown';
import FleetModelBreakdown from './FleetModelBreakdown';
import IssuedInvoices from '../invoices/IssuedInvoices';
import { getISOWeek, getISOWeeksInYear, formatWeekRange, buildWeekPeriodFilter } from '../../utils/payoutWeek';
import './FleetPaymentsPage.css';

// Faturalanacak tutarı ≤ 0 olan dönem için gösterilen açıklama (sunucudaki
// FleetInvoiceAmountRule mesajıyla aynı gerekçe).
const NO_INVOICE_REASON =
    'Bu dönemde faturalanacak tutar oluşmadı: zaman-bazlı kurye gideri paket gelirine eşit ya da ondan yüksek. ' +
    'Hızlıyo\'ya kesilecek fatura yok, bu hakediş için fatura beklenmiyor.';

/**
 * @param embedded Hakedişler ekranında sekme olarak açıldığında kendi başlığını çizmez.
 * @param onWeekContext Seçili hafta + o haftanın hakediş durumunu üst ekrana bildirir;
 *        akış bandı bunu gösterir (yoksa bant seçili haftadan bağımsız kalırdı).
 */
const FleetPaymentsPage = ({ embedded = false, onWeekContext }) => {
    const now = getISOWeek(new Date());
    const [selectedYear, setSelectedYear] = useState(now.year);
    const [selectedWeek, setSelectedWeek] = useState(now.week);

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('CreatedAtUtc');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});
    const [filterTypes, setFilterTypes] = useState({});


    const [detailData, setDetailData] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [paymentLines, setPaymentLines] = useState([]);
    const [linesLoading, setLinesLoading] = useState(false);
    // "hiç yüklenmedi" ile "yüklendi ama 0 kayıt döndü" durumlarını ayırmak için ayrı bayrak —
    // yalnızca lines.length===0'a bakarsak gerçekten boş dönen hakedişlerde akordiyon her açılışta
    // API'ye yeniden istek atar. İstek başarısız olursa bayrak set edilmez ki kullanıcı tekrar deneyebilsin.
    const [linesLoaded, setLinesLoaded] = useState(false);

    const loadPaymentLines = async (fleetPaymentId) => {
        setLinesLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.FLEET_PAYMENT_DELIVERIES(fleetPaymentId));
            if (res.success) {
                setPaymentLines(res.data || []);
                setLinesLoaded(true);
            }
        } catch { /* yut */ } finally { setLinesLoading(false); }
    };

    const [uploadTarget, setUploadTarget] = useState(null);
    const [uploadForm, setUploadForm] = useState({ invoiceNumber: '', invoiceDate: '', invoiceAmount: '' });
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [paymentInvoices, setPaymentInvoices] = useState([]);

    const formatCurrency = (amount) => {
        if (amount == null) return '0 TL';
        return formatTL(amount);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDateShort = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('tr-TR');
    };

    const navigateWeek = (dir) => {
        let w = selectedWeek + dir;
        let y = selectedYear;
        if (w < 1) {
            y--;
            w = getISOWeeksInYear(y);
        } else if (w > getISOWeeksInYear(y)) {
            y++;
            w = 1;
        }
        setSelectedYear(y);
        setSelectedWeek(w);
        setCurrentPage(1);
    };

    const goToCurrentWeek = () => {
        const c = getISOWeek(new Date());
        setSelectedYear(c.year);
        setSelectedWeek(c.week);
        setCurrentPage(1);
    };

    const isCurrentWeek = selectedYear === now.year && selectedWeek === now.week;

    const buildFilterModel = useCallback(() => {
        const fm = buildWeekPeriodFilter(selectedYear, selectedWeek);
        Object.keys(filters).forEach((key) => {
            if (filters[key] != null && String(filters[key]).trim() !== '') {
                fm[key] = { filter: filters[key], type: filterTypes[key] || 'contains', caseSensitive: false };
            }
        });
        return fm;
    }, [selectedYear, selectedWeek, filters, filterTypes]);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.FLEET_PAYMENTS_LIST, {
                page: currentPage,
                pageSize,
                sortModels: sortField ? [{ colId: sortField, sort: sortOrder }] : null,
                filterModel: buildFilterModel()
            });
            if (res.success && res.data) {
                const data = res.data.data || res.data;
                setPayments(data.items || []);
                setTotalRecords(data.totalCount || 0);
            } else {
                setError(res.error || 'Veriler yüklenemedi');
            }
        } catch {
            setError('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortField, sortOrder, buildFilterModel]);

    // Tüm-dönem özeti (PAYMENT_SUMMARY) bu ekranda ARTIK ÇAĞRILMIYOR: kartlar seçili
    // haftanın kayıtlarından türüyor. Özet, Faturalar sekmesinde kendi kapsamıyla duruyor.
    useEffect(() => { fetchPayments(); }, [fetchPayments]);

    /* Akış bandına seçili haftanın durumunu bildir. Filo için dönemde tek kayıt açılır;
       yine de en GERİ adımı bildiririz — akış, en geride kalan kayıt neredeyse oradadır. */
    useEffect(() => {
        if (!onWeekContext) return;
        const statuses = payments.map(p => p.status).filter(s => s != null);
        onWeekContext({
            year: selectedYear,
            week: selectedWeek,
            status: statuses.length ? Math.min(...statuses) : null,
        });
    }, [onWeekContext, selectedYear, selectedWeek, payments]);

    useEffect(() => {
        const notifTypes = ['FleetPaymentCreated', 'FleetInvoiceApproved', 'FleetInvoiceRejected'];
        const handleNotification = (event) => {
            const data = event.detail;
            if (notifTypes.includes(data?.type)) {
                fetchPayments();
            }
        };
        const eventName = `fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION}`;
        window.addEventListener(eventName, handleNotification);
        return () => window.removeEventListener(eventName, handleNotification);
    }, [fetchPayments]);

    const handleSort = (field, order) => { setSortField(field); setSortOrder(order); };
    const handleFilterChange = (field, value, type) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setFilterTypes(prev => ({ ...prev, [field]: type }));
        setCurrentPage(1);
    };
    const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

    const handleRefresh = () => {
        fetchPayments();
    };

    const openUpload = async (item) => {
        setUploadTarget(item);
        setUploadForm({
            invoiceNumber: '',
            invoiceDate: new Date().toISOString().slice(0, 10),
            invoiceAmount: item.totalFleetShareAmount ?? 0,   // beklenen tutar ön-dolu
            file: null
        });
        setUploadFile(null);
        setUploadError('');
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.FLEET_INVOICE_LIST(item.id));
            if (res.success && res.data?.data) setPaymentInvoices(res.data.data);
            else setPaymentInvoices([]);
        } catch { setPaymentInvoices([]); }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { setUploadError('Dosya boyutu en fazla 10 MB olabilir.'); return; }
        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) { setUploadError('Sadece PDF, JPEG veya PNG kabul edilir.'); return; }
        setUploadError('');
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            setUploadFile({ base64, name: file.name, type: file.type, size: file.size });
        };
        reader.readAsDataURL(file);
    };

    const handleUploadSubmit = async () => {
        if (!uploadForm.invoiceNumber.trim()) { setUploadError('Fatura numarası zorunludur.'); return; }
        if (!uploadForm.invoiceDate) { setUploadError('Fatura tarihi zorunludur.'); return; }
        if (!uploadFile) { setUploadError('Fatura dosyası seçiniz.'); return; }
        setUploadLoading(true);
        setUploadError('');
        try {
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.FLEET_INVOICE_UPLOAD, {
                fleetPaymentId: uploadTarget.id,
                invoiceNumber: uploadForm.invoiceNumber,
                invoiceDate: uploadForm.invoiceDate,
                invoiceAmount: Number(uploadForm.invoiceAmount) || 0,
                invoiceFileBase64: uploadFile.base64,
                originalFileName: uploadFile.name,
                contentType: uploadFile.type,
            });
            if (res.success) {
                setUploadTarget(null);
                fetchPayments();
            } else {
                setUploadError(res.error || res.message || 'Yükleme başarısız');
            }
        } catch { setUploadError('Beklenmeyen hata oluştu'); }
        finally { setUploadLoading(false); }
    };

    const openDetail = (item) => {
        setDetailData(item);
        setShowDetail(true);
    };

    const closeDetail = () => {
        setShowDetail(false);
        setDetailData(null);
        setPaymentLines([]);
        setLinesLoaded(false);
    };

    // İstemci tarafı tutar kapısı — sunucu kuralıyla aynı tolerans (±0,05 TL). Karşılaştırma
    // kuruş tam sayısı üzerinden yapılır (bkz. isAmountWithinTolerance); uyarı metni ve gönder
    // düğmesinin disabled koşulu TEK bu değişkenden beslenir.
    const amountMismatch = uploadTarget != null &&
        !isAmountWithinTolerance(uploadForm.invoiceAmount, uploadTarget?.totalFleetShareAmount);

    /* Kartlar SEÇİLİ HAFTAYI sayar, tüm dönemleri değil: "1.156,80 TL var ama liste 0 kayıt"
       çelişkisi buradan doğuyordu — kart tüm dönemlerin toplamını, liste tek haftayı gösteriyordu.
       Kaynak listenin kendisi; filo başına dönemde tek kayıt açıldığı için sayfa toplamı = hafta
       toplamıdır. Fatura sayacı kaldırıldı: dönem kaydı fatura bilgisi taşımıyor (FleetPaymentResponseDto),
       yalnızca tüm-dönem özetinden gelebiliyordu. Fatura durumu Faturalar sekmesinde. */
    const weekStats = useMemo(() => {
        const paid = payments.filter(p => p.status === 3);
        const pending = payments.filter(p => p.status !== 3);
        return {
            earnings: payments.reduce((t, p) => t + (p.totalFleetShareAmount || 0), 0),
            pending: pending.reduce((t, p) => t + (p.totalFleetShareAmount || 0), 0),
            paid: paid.reduce((t, p) => t + (p.totalFleetShareAmount || 0), 0),
            deliveries: payments.reduce((t, p) => t + (p.totalDeliveries || 0), 0),
        };
    }, [payments]);

    const statCards = [
        { title: 'Hakediş tutarı', value: formatCurrency(weekStats.earnings), icon: TlIcon, color: '#22c55e', subtitle: 'seçili hafta' },
        { title: 'Bekleyen', value: formatCurrency(weekStats.pending), icon: Clock, color: '#f59e0b', subtitle: 'seçili hafta' },
        { title: 'Ödenen', value: formatCurrency(weekStats.paid), icon: CheckCircle, color: '#6366f1', subtitle: 'seçili hafta' },
        { title: 'Teslimat', value: weekStats.deliveries, icon: Package, color: '#3b82f6', subtitle: 'seçili hafta' },
    ];

    const columns = useMemo(() => [
        {
            field: 'period', header: 'Dönem', sortable: true,
            render: (row) => (
                <div className="fp-period-cell">
                    <span className="fp-period-text">{row.period || '-'}</span>
                    {row.periodRange && <span className="fp-period-range">{row.periodRange}</span>}
                </div>
            )
        },
        {
            field: 'totalDeliveries', header: 'Teslimat', sortable: true,
            render: (row) => <span className="fp-delivery-badge">{row.totalDeliveries || 0}</span>
        },
        {
            field: 'totalFleetShareAmount', header: 'Filo Kazancı', sortable: true,
            render: (row) => <span className="fp-amount-highlight">{formatCurrency(row.totalFleetShareAmount)}</span>
        },
        {
            field: 'totalDeliveries', header: 'Ort/Teslimat', sortable: false,
            render: (row) => {
                const avg = row.totalDeliveries > 0 ? row.totalFleetShareAmount / row.totalDeliveries : 0;
                return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatCurrency(avg)}</span>;
            }
        },
        {
            field: 'status', header: 'Durum', sortable: true,
            render: (row) => {
                const st = getFleetPaymentStatus(row.status);
                return <span className={`fp-status-badge ${st.tone}`}>{st.label}</span>;
            }
        },
        { field: 'createdAtUtc', header: 'Tarih', sortable: true, render: (row) => formatDateShort(row.createdAtUtc) },
        {
            field: 'actions', header: '', sortable: false,
            render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {/* Faturalanacak tutar ≤ 0 ise fatura KESİLEMEZ (sunucu da reddeder):
                        düğme yerine sebebi yazan kısa bir bilgi gösterilir. */}
                    {canUploadFleetInvoice(row.status) && (
                        hasInvoiceableAmount(row.totalFleetShareAmount) ? (
                            <button className="fp-detail-btn" style={{ color: '#22c55e' }} onClick={(e) => { e.stopPropagation(); openUpload(row); }} title="Fatura Yükle">
                                <Upload size={14} />
                            </button>
                        ) : (
                            <span className="fp-no-invoice" title={NO_INVOICE_REASON}>Fatura kesilmez</span>
                        )
                    )}
                    <button className="fp-detail-btn" onClick={(e) => { e.stopPropagation(); openDetail(row); }} title="Detay">
                        <Eye size={14} />
                    </button>
                </div>
            )
        }
    ], []);

    const renderCard = (item) => {
        const st = getFleetPaymentStatus(item.status);
        return (
            <div className="fp-payment-card" onClick={() => openDetail(item)}>
                <div className="fp-card-header">
                    <div>
                        <h3>{item.period || 'Dönem'}</h3>
                        <p>{item.periodRange}</p>
                    </div>
                    <span className={`fp-status-badge ${st.tone}`}>{st.label}</span>
                </div>
                <div className="fp-card-stats">
                    <div className="fp-card-stat">
                        <span className="fp-card-stat-label">Teslimat</span>
                        <span className="fp-card-stat-value">{item.totalDeliveries || 0}</span>
                    </div>
                    <div className="fp-card-stat highlight">
                        <span className="fp-card-stat-label">Filo Kazancı</span>
                        <span className="fp-card-stat-value">{formatCurrency(item.totalFleetShareAmount)}</span>
                    </div>
                    <div className="fp-card-stat">
                        <span className="fp-card-stat-label">Ort/Teslimat</span>
                        <span className="fp-card-stat-value">{item.totalDeliveries > 0 ? formatCurrency(item.totalFleetShareAmount / item.totalDeliveries) : '0 TL'}</span>
                    </div>
                </div>
                {canUploadFleetInvoice(item.status) && !hasInvoiceableAmount(item.totalFleetShareAmount) && (
                    <div className="fp-card-note" title={NO_INVOICE_REASON}>
                        Fatura kesilmez — bu dönemde faturalanacak tutar oluşmadı.
                    </div>
                )}
            </div>
        );
    };

    const renderDetail = () => {
        if (!showDetail || !detailData) return null;
        const d = detailData;
        const st = getFleetPaymentStatus(d.status);
        return (
            <div className="fp-detail-overlay" onClick={closeDetail}>
                <div className="fp-detail-panel" onClick={e => e.stopPropagation()}>
                    <div className="fp-detail-header">
                        <h2>Hakediş Detayı</h2>
                        <button className="fp-detail-close" onClick={closeDetail}><X size={18} /></button>
                    </div>
                    <div className="fp-detail-body">
                        <div className="fp-detail-section fp-detail-top">
                            <div className="fp-detail-period-info">
                                <span className="fp-detail-period-title">{d.period}</span>
                                {d.periodRange && <span className="fp-detail-period-range">{d.periodRange}</span>}
                            </div>
                            <span className={`fp-status-badge ${st.tone}`}>{st.label}</span>
                        </div>

                        <div className="fp-detail-section">
                            <FleetPaymentBreakdown
                                payment={d}
                                lines={paymentLines}
                                linesLoading={linesLoading}
                                linesLoaded={linesLoaded}
                                onLoadLines={() => loadPaymentLines(d.id)}
                            />
                        </div>

                        <div className="fp-detail-section">
                            <h3><TlIcon size={14} /> Finansal Özet</h3>
                            <div className="fp-detail-grid">
                                <div className="fp-detail-item">
                                    <span>Teslimat Sayısı</span>
                                    <strong>{d.totalDeliveries}</strong>
                                </div>
                                <div className="fp-detail-item highlight">
                                    <span>Filo Kazancı</span>
                                    <strong>{formatCurrency(d.totalFleetShareAmount)}</strong>
                                </div>
                                <div className="fp-detail-item">
                                    <span>Ort. Teslimat Başı</span>
                                    <strong>{d.totalDeliveries > 0 ? formatCurrency(d.totalFleetShareAmount / d.totalDeliveries) : '-'}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="fp-detail-section">
                            <h3><Calendar size={14} /> Dönem Bilgisi</h3>
                            <div className="fp-detail-grid">
                                <div className="fp-detail-item">
                                    <span>Yıl</span>
                                    <strong>{d.year}</strong>
                                </div>
                                {d.weekNumber && (
                                    <div className="fp-detail-item">
                                        <span>Hafta</span>
                                        <strong>Hafta {d.weekNumber}</strong>
                                    </div>
                                )}
                                {d.month && (
                                    <div className="fp-detail-item">
                                        <span>Ay</span>
                                        <strong>{d.month}</strong>
                                    </div>
                                )}
                                <div className="fp-detail-item">
                                    <span>Dönem Aralığı</span>
                                    <strong>{formatDateShort(d.periodStart)} - {formatDateShort(d.periodEnd)}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="fp-detail-section">
                            <h3><Clock size={14} /> Tarihler</h3>
                            <div className="fp-detail-timeline">
                                {d.createdAtUtc && (
                                    <div className="fp-timeline-item">
                                        <span className="fp-timeline-dot created" />
                                        <div>
                                            <span className="fp-timeline-label">Oluşturulma</span>
                                            <span className="fp-timeline-date">{formatDate(d.createdAtUtc)}</span>
                                        </div>
                                    </div>
                                )}
                                {d.approvedAt && (
                                    <div className="fp-timeline-item">
                                        <span className="fp-timeline-dot approved" />
                                        <div>
                                            <span className="fp-timeline-label">Onaylanma</span>
                                            <span className="fp-timeline-date">{formatDate(d.approvedAt)}</span>
                                        </div>
                                    </div>
                                )}
                                {d.paidAt && (
                                    <div className="fp-timeline-item">
                                        <span className="fp-timeline-dot paid" />
                                        <div>
                                            <span className="fp-timeline-label">Ödeme</span>
                                            <span className="fp-timeline-date">{formatDate(d.paidAt)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {d.paymentReference && (
                                <div className="fp-detail-ref">Referans: {d.paymentReference}</div>
                            )}
                            {d.adminNote && (
                                <div className="fp-detail-note">Not: {d.adminNote}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="fp-container">
            {!embedded && (
                <div className="page-header">
                    <div className="header-left">
                        <div className="page-icon"><Wallet size={20} /></div>
                        <div className="page-title">
                            <h1>Filo Hakedişleri</h1>
                            <p>Haftalık filo hakediş takibi ve ödeme durumu</p>
                        </div>
                    </div>
                    <button className="btn btn-secondary" onClick={handleRefresh}>
                        <RefreshCw size={16} />
                        Yenile
                    </button>
                </div>
            )}

            <div className="fp-week-nav">
                <button className="fp-week-btn" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className="fp-week-display">
                    <span className="fp-week-label">Hafta {selectedWeek}, {selectedYear}</span>
                    <span className="fp-week-range">{formatWeekRange(selectedYear, selectedWeek)}</span>
                </div>
                <button className="fp-week-btn" onClick={() => navigateWeek(1)}>
                    <ChevronRight size={20} />
                </button>
                {!isCurrentWeek && (
                    <button className="fp-today-btn" onClick={goToCurrentWeek}>
                        Bu Hafta
                    </button>
                )}
            </div>

            {error && (
                <Notice tone="error" title="Hakedişler yüklenemedi">
                    {error} Bağlantınızı kontrol edip Yenile'ye basın.
                </Notice>
            )}

            <FleetModelBreakdown year={selectedYear} week={selectedWeek} />

            <IssuedInvoices year={selectedYear} week={selectedWeek} />

            <div className="fp-section-head">
                <h2>Hızlıyo'dan alacağınız</h2>
                <p>Hızlıyo'nun tahsil ettiği paketler (Model 1) hafta kapanınca hakediş kaydına dönüşür.</p>
            </div>

            <div className="fp-stats-grid">
                {statCards.map((card, i) => {
                    const Icon = card.icon;
                    return (
                        <div key={i} className="fp-stat-card">
                            <div className="fp-stat-icon">
                                <Icon size={20} />
                            </div>
                            <div className="fp-stat-content">
                                <span className="fp-stat-label">{card.title}</span>
                                <span className="fp-stat-value">{loading ? '...' : card.value}</span>
                                {card.subtitle && <span className="fp-stat-sub">{card.subtitle}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="fp-grid-section">
                <div className="fp-grid-header">
                    <h2>Hakediş kayıtları</h2>
                    <div className="fp-grid-tools">
                        <span className="fp-grid-count">{totalRecords} kayıt</span>
                        {embedded && (
                            <button className="btn btn-secondary btn-sm" onClick={handleRefresh}>
                                <RefreshCw size={14} />
                                Yenile
                            </button>
                        )}
                    </div>
                </div>
                <DataGrid
                    data={payments}
                    columns={columns}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={handlePageSizeChange}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    filters={filters}
                    filterTypes={filterTypes}
                    onFilterChange={handleFilterChange}
                    renderCard={renderCard}
                    emptyIcon={Wallet}
                    emptyMessage={isCurrentWeek ? 'Bu hafta henüz kapanmadı.' : 'Bu haftaya ait hakediş kaydı yok.'}
                    emptyHint={
                        <ul>
                            <li>
                                Hakediş kaydı yalnız <strong>Hızlıyo'nun tahsil ettiği</strong> (Model 1) paketler
                                için açılır. Diğer iki modelde paket bedelini restorandan doğrudan alırsınız;
                                yukarıdaki kartlarda görünür.
                            </li>
                            {isCurrentWeek ? (
                                <li>
                                    Hakediş yalnız <strong>tamamlanmış</strong> haftalar için üretilir. Yürüyen
                                    haftanın kaydı <strong>Pazartesi 07:00</strong>'de oluşur; üstteki hatta kalan
                                    süre yazıyor.
                                </li>
                            ) : (
                                <li>
                                    O hafta filonuza ait <strong>tamamlanmış teslimat</strong> yoksa kayıt üretilmez.
                                </li>
                            )}
                            <li>
                                Teslimat, kurye <strong>filonuza onaylı üyeyken</strong> yapılmış olmalı — üyelik
                                dışındaki teslimatlar filo hakedişine girmez.
                            </li>
                            <li>Filo payı <strong>0,00 TL</strong> çıkan dönemler için kayıt açılmaz.</li>
                        </ul>
                    }
                    loadingMessage="Yükleniyor..."
                />
            </div>

            {uploadTarget && (
                <div className="fp-detail-overlay" onClick={() => !uploadLoading && setUploadTarget(null)}>
                    <div className="fp-detail-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
                        <div className="fp-detail-header">
                            <h2><Upload size={18} /> Fatura Yükle</h2>
                            <button className="fp-detail-close" onClick={() => !uploadLoading && setUploadTarget(null)}><X size={18} /></button>
                        </div>
                        <div className="fp-detail-body">
                            <div className="fp-detail-section fp-detail-top">
                                <div className="fp-detail-period-info">
                                    <span className="fp-detail-period-title">{uploadTarget.period}</span>
                                    <span className="fp-detail-period-range">{uploadTarget.periodRange}</span>
                                </div>
                                <span className="fp-amount-highlight" style={{ fontSize: 16 }}>{formatCurrency(uploadTarget.totalFleetShareAmount)}</span>
                            </div>

                            {paymentInvoices.length > 0 && (
                                <div className="fp-detail-section">
                                    <h3><FileText size={14} /> Yüklenen Faturalar</h3>
                                    {paymentInvoices.map(inv => {
                                        const ist = getFleetPaymentStatus(inv.status);
                                        return (
                                            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
                                                <FileText size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                                <span style={{ flex: 1, fontSize: 12 }}>{inv.invoiceNumber}</span>
                                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatCurrency(inv.invoiceAmount)}</span>
                                                <span className={`fp-status-badge ${ist.tone}`} style={{ fontSize: 10, padding: '2px 6px' }}>{ist.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="fp-detail-section">
                                <h3><Upload size={14} /> Yeni Fatura</h3>
                                {uploadError && <div className="fp-amount-warn" style={{ marginTop: 0, marginBottom: 10 }}>{uploadError}</div>}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div className="form-group">
                                        <label>Fatura Numarası <span className="required">*</span></label>
                                        <input type="text" value={uploadForm.invoiceNumber} onChange={e => setUploadForm(p => ({ ...p, invoiceNumber: e.target.value }))} placeholder="FTR-2026-001" disabled={uploadLoading} />
                                    </div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>Fatura Tarihi <span className="required">*</span></label>
                                            <input type="date" value={uploadForm.invoiceDate} onChange={e => setUploadForm(p => ({ ...p, invoiceDate: e.target.value }))} disabled={uploadLoading} />
                                        </div>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label>Tutar (TL)</label>
                                            <input type="number" value={uploadForm.invoiceAmount} onChange={e => setUploadForm(p => ({ ...p, invoiceAmount: e.target.value }))} step="0.01" min="0" disabled={uploadLoading} />
                                        </div>
                                    </div>
                                    {amountMismatch && (
                                        <div className="fp-amount-warn">
                                            Fatura tutarı {formatTL(uploadTarget?.totalFleetShareAmount ?? 0)} olmalı.
                                            {(uploadTarget?.totalPlatformFee ?? 0) > 0 && ' Aradaki fark platform kesintisi ve KDV\'sidir; fatura NET tutara kesilir.'}
                                        </div>
                                    )}
                                    <FleetPaymentBreakdown payment={uploadTarget} />
                                    <div>
                                        <label style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Fatura Dosyası * (PDF, JPEG, PNG - max 10MB)</label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 12, border: '1px dashed var(--border-hover)', cursor: 'pointer', background: uploadFile ? 'rgba(34,197,94,0.06)' : 'var(--bg-tertiary)', transition: 'all 0.15s' }}>
                                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} disabled={uploadLoading} style={{ display: 'none' }} />
                                            {uploadFile ? <FileText size={16} style={{ color: '#22c55e' }} /> : <Upload size={16} style={{ color: 'var(--text-muted)' }} />}
                                            <span style={{ fontSize: 12, color: uploadFile ? '#22c55e' : 'var(--text-muted)' }}>
                                                {uploadFile ? `${uploadFile.name} (${(uploadFile.size / 1024).toFixed(0)} KB)` : 'Dosya seçmek için tıklayın'}
                                            </span>
                                        </label>
                                    </div>
                                    <button className="btn btn-primary" onClick={handleUploadSubmit} disabled={uploadLoading || amountMismatch}>
                                        {uploadLoading ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Yükleniyor...</> : <><Upload size={14} /> Faturayı Yükle</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {renderDetail()}
        </div>
    );
};

export default FleetPaymentsPage;
